"use client";

import { useEffect } from "react";
import { toast } from "sonner";

type UpdateInfo = {
	version: string;
	releaseDate?: string;
};

type UpdaterApi = {
	onUpdateAvailable: (callback: (info: UpdateInfo) => void) => void;
	onDownloadProgress: (
		callback: (progress: {
			percent: number;
			bytesPerSecond: number;
			total: number;
			transferred: number;
		}) => void
	) => void;
	onUpdateDownloaded: (callback: (info: UpdateInfo) => void) => void;
	onUpdateNotAvailable: (callback: () => void) => void;
	onError: (callback: (error: string) => void) => void;
	installUpdate: () => Promise<void>;
};

function getUpdaterApi(): UpdaterApi | null {
	if (typeof window !== "undefined" && "api" in window) {
		const api = window.api as { updater?: UpdaterApi };
		return api.updater ?? null;
	}
	return null;
}

export function AutoUpdater() {
	useEffect(() => {
		const updater = getUpdaterApi();
		console.log(
			"[AutoUpdater] Initializing, updater API:",
			updater ? "found" : "not found"
		);
		if (!updater) {
			return;
		}

		console.log("[AutoUpdater] Registering event listeners");

		updater.onUpdateAvailable((info) => {
			console.log("[AutoUpdater] Update available:", info);
		});

		updater.onDownloadProgress((progress) => {
			console.log("[AutoUpdater] Download progress:", progress.percent, "%");
		});

		updater.onUpdateDownloaded((info) => {
			console.log("[AutoUpdater] Update downloaded:", info);
			toast.info("A new app version is available", {
				description: "Restart to apply the update",
				duration: Number.POSITIVE_INFINITY,
				dismissible: true,
				action: {
					label: "Restart Now",
					onClick: () => {
						updater.installUpdate();
					},
				},
			});
		});

		updater.onUpdateNotAvailable(() => {
			console.log("[AutoUpdater] No update available");
			toast.info("You're up to date", {
				description: "No updates available",
			});
		});

		updater.onError((error) => {
			console.error("[AutoUpdater] Error:", error);
		});
	}, []);

	return null;
}
